'use client';

import { useCallback, useRef } from 'react';
import { useTranslations } from 'next-intl';
import { Position } from '@headless-workspace/config';
import { ContentAssetKeys, unwrapResultSuccess } from '@headless-workspace/core-domain';
import { SideMenuModal, useUIContext } from '@headless-workspace/core-ui';
import { GlowModal } from '@headless-workspace/glow-ds';
import { InnerHTML, ProductDetailInfoModal, useDeliveryReturnsModalMutation } from '../../../../client/';
import { HTMLString } from '../../../../server';

type UseDeliveryReturnsModalOptions = {
    translationsErrorMessage?: string;
    notifyError?: (message: string) => void;
};

const PRODUCT_DETAIL_INFO_MODAL_ID = 'product-detail-info-modal';
const MODAL_CLASS_NAME = 'flex flex-col bg-background-brand overflow-hidden';

export const useDeliveryReturnsModal = ({
    translationsErrorMessage,
    notifyError,
}: UseDeliveryReturnsModalOptions = {}) => {
    const htmlContentRef = useRef<HTMLString | null>(null);
    const titleRef = useRef<string>('');

    const {
        menuModal: { addAndOpen, close },
    } = useUIContext();

    const { get: getDeliveryInformation, isMutating } = useDeliveryReturnsModalMutation();

    const t = useTranslations('Common');

    const createModal = useCallback(
        (modalTitle: string, modalHtmlContent: HTMLString) => (
            <ProductDetailInfoModal
                title={modalTitle}
                closeButtonLabel={t('action.label.close')}
                productModalId={PRODUCT_DETAIL_INFO_MODAL_ID}
                renderInformation={
                    <GlowModal.Content>
                        <InnerHTML rawHtml={modalHtmlContent} />
                    </GlowModal.Content>
                }
                isSidePanelDeliveryZone={true}
                removeModalOnClose={true}
            />
        ),
        [t],
    );

    const openModal = useCallback(async () => {
        const existingHtml = htmlContentRef.current;
        const existingTitle = titleRef.current;

        if (existingHtml) {
            return addAndOpen(
                PRODUCT_DETAIL_INFO_MODAL_ID,
                SideMenuModal(
                    Position.Right,
                    { default: createModal(existingTitle, existingHtml) },
                    MODAL_CLASS_NAME,
                    true,
                    true,
                ),
            );
        }

        const deliveryInformationResult = await getDeliveryInformation();
        const deliveryInformation = unwrapResultSuccess(deliveryInformationResult);
        if (!deliveryInformation) {
            if (notifyError && translationsErrorMessage) {
                notifyError(translationsErrorMessage);
            }
            return;
        }

        const deliveryPopupContent = deliveryInformation.find(
            (asset) => asset.id === ContentAssetKeys.CartDeliveryPopup,
        )?.content;

        const content = deliveryPopupContent?.html?.content;
        if (!content) return;

        const parsedHtml = HTMLString(content);
        const parsedTitle = deliveryPopupContent.title ?? '';

        htmlContentRef.current = parsedHtml;
        titleRef.current = parsedTitle;

        addAndOpen(
            PRODUCT_DETAIL_INFO_MODAL_ID,
            SideMenuModal(
                Position.Right,
                { default: createModal(parsedTitle, parsedHtml) },
                MODAL_CLASS_NAME,
                true,
                true,
            ),
        );
    }, [addAndOpen, createModal, getDeliveryInformation, notifyError, translationsErrorMessage]);

    const closeModal = useCallback(() => {
        close(PRODUCT_DETAIL_INFO_MODAL_ID, true);
    }, [close]);

    return {
        openModal,
        closeModal,
        isMutating,
    };
};
